import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import CommonSelectorModal from "../../../../components/common/form/CommonSelectorModal";
import ModalButton from "../../../../components/common/form/ModalButton";
import IconButton from "../../../../components/common/form/IconButton";
import { FaLink, FaSearch, FaTimes } from "react-icons/fa";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import {
    useGetEnumsQuery
} from "../../../../app/globalApi";
import { Routes as AppRoutes } from "../../../../routes";
import { useGetAllStatesQuery } from "../../../data/states/statesApi";
import { useCreateManagementCompanyApiMutation } from "../managementCompany";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { useSearchThirdPartyProviderApiQuery } from "../../thirdPartyProvider/thirdPartyProvider";
import NumberFormatField from "../../../../components/common/form/NumberFormatField";
import { createOptionListForSelectTag, postalPattern, postalTitle } from "../../../../helpers/utils/Utils";
import SelectField from "../../../../components/common/form/SelectField";
import TextAreaField from "../../../../components/common/form/TextAreaField";
import { FaMagnifyingGlass } from "react-icons/fa6";

const CreateManagementCompany: FC = () => {
    const [crumbs] = useState([
        { content: "Home", url: "/" },
        { content: "Management Companies", url: "/management-companies" },
        { content: "Add Management Companies", url: "" },
    ]);

    const navigate = useNavigate();
    const [managementCompanyData, setManagementCompanyData] = useState<any>({});
    const { data: enums } = useGetEnumsQuery("");
    const { data: stateData } = useGetAllStatesQuery("");
    const [createApi, createApiResponse] = useCreateManagementCompanyApiMutation();
    const iconButtonStyle = "fa cursor-pointer text-highlight-0 absolute left-auto right-5 top-6";
    const [selectedRow, setSelectedRow] = useState<any>();
    const [showProviderModal, setShowProviderModal] = useState<boolean>(false);

    useEffect(() => {
        if (managementCompanyData.customerLevel?.value === "TENANT") {
            setManagementCompanyData((prevData: any) => ({
                ...prevData,
                tenantId: null,
            }));
        }
    }, [managementCompanyData.customerLevel]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const obj = {
                name: managementCompanyData.name,
                hoaThirdPartyProvider: selectedRow ? {
                    id: selectedRow.id
                } : null,
                contacts: [
                    {
                        name: managementCompanyData?.contactName,
                        email: managementCompanyData?.email,
                        website: managementCompanyData?.website,
                        phone: managementCompanyData?.phone,
                        fax: managementCompanyData?.fax,
                        preferredContactMethod: managementCompanyData?.contactMethod?.value,
                        note: managementCompanyData?.note,
                        address: {
                            mailingAddress: managementCompanyData?.mailingAddress,
                            zipCode: managementCompanyData?.zipCode,
                            city: managementCompanyData?.city,
                            state: managementCompanyData?.state?.value
                        }
                    }
                ]
            }

            const create = async () => {
                return await createApi(obj).unwrap();
            };

            await handleRTKQuery(create, (response: any) => {
                if (response?.success || response?.data?.success) {
                    setManagementCompanyData({});
                    navigate(AppRoutes.ManagementCompany.path);
                }
            });
        } catch (error) {
            console.error("Error creating management company:", error);
        }
    };


    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | {
        name: string;
        value: any
    } | ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = "target" in e ? e.target : e;
        setManagementCompanyData({
            ...managementCompanyData,
            [name]: value,
        })
    }

    const handleOnClick = (e: any, name: string) => {
        e.preventDefault();
        setManagementCompanyData({
            ...managementCompanyData,
            hoaThirdPartyProvider: selectedRow.name
        })
        setShowProviderModal(false)
    }

    const handleClearThirdPartyProvider = () => {
        setManagementCompanyData((prevData: any) => ({
            ...prevData,
            hoaThirdPartyProvider: "",
        }));
    };

    const columns: DataTableColumn[] = [
        {
            label: "Name",
            accessor: "name",
            filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''} />
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }],
        },
    ];

    return (
        <>
            <div className="adduser_container px-4 py-2">
                <Breadcrumb crumbs={crumbs} />
                <div className="flex justify-center mt-3 text-sm">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">
                            Add Management Company
                        </h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12 adduser_Wrapper">
                                <InputField
                                    name={"name"}
                                    label="Name"
                                    onChange={handleOnChange}
                                    value={managementCompanyData.name || ""}
                                    type="text"
                                    placeholder="Enter Name..."
                                    required={true}
                                />
                                <InputField
                                    name="hoaThirdPartyProvider"
                                    label="Third Party Provider"
                                    onChange={handleOnChange}
                                    value={managementCompanyData?.hoaThirdPartyProvider || ""}
                                    type="text"
                                    placeholder="Enter Third Party Provider..."
                                    icon={<IconButton
                                        icon={<FaLink onClick={() => {
                                            if (selectedRow?.id) {
                                                window.open(`/third-party-providers/${selectedRow.id}`, '_blank');
                                            }
                                        }} />
                                        }
                                        className={`text-sm  ${managementCompanyData?.hoaThirdPartyProvider ? 'text-highlight-4' : 'text-surface-4 cursor-default'
                                            }`}
                                        title={"Go to third Party Provider"}
                                    />}
                                    onClick={() => {
                                        setShowProviderModal(true);
                                    }}
                                    className="cursor-pointer"
                                    inputIcon={
                                        managementCompanyData?.hoaThirdPartyProvider ? (
                                            <FaTimes
                                                className={iconButtonStyle}
                                                title="Clear Data"
                                                onClick={() => {
                                                    handleClearThirdPartyProvider();

                                                }}
                                            />

                                        ) :
                                            <FaSearch
                                                className={iconButtonStyle}
                                                title="Add Provider Detail"
                                                onClick={() => {
                                                    setShowProviderModal(true);
                                                }}
                                            />

                                    }
                                />
                                <InputField
                                    label="Contact Name"
                                    name="contactName"
                                    value={managementCompanyData?.contactName || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    required
                                />
                                <InputField
                                    label="Mailing Address"
                                    name="mailingAddress"
                                    value={managementCompanyData?.mailingAddress || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    required
                                />
                                <NumberFormatField
                                    className=""
                                    placeholder="Enter Phone..."
                                    name="phone"
                                    value={managementCompanyData?.phone || ""}
                                    onChange={handleOnChange}
                                    label={"Phone"}
                                    required
                                />
                                <InputField
                                    label="Email"
                                    name="email"
                                    value={managementCompanyData?.email || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className="mb-2"
                                />
                                <InputField
                                    label="Website"
                                    name="website"
                                    value={managementCompanyData?.website || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className="mb-2"
                                />
                                <NumberFormatField className="" placeholder="Enter Fax..."
                                    value={managementCompanyData?.fax || ""}
                                    onChange={handleOnChange}
                                    name="fax"
                                    label={"Fax"}
                                />

                                <InputField
                                    label="Zip Code"
                                    name="zipcode"
                                    value={managementCompanyData?.zipcode || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className="mb-2"
                                    pattern={postalPattern} title={postalTitle}
                                />

                                <SelectField
                                    placeholder="Select State..."
                                    value={managementCompanyData?.state || null}
                                    options={createOptionListForSelectTag(stateData, "stateName", "stateAbbreviation")}
                                    onChange={(selectedOption) => handleOnChange({
                                        name: "state",
                                        value: selectedOption
                                    })}
                                    label="State"
                                />
                                <InputField
                                    label="City"
                                    name="city"
                                    value={managementCompanyData?.city || ""}
                                    type="text"
                                    onChange={handleOnChange}
                                    className="mb-2"
                                />
                                <SelectField
                                    placeholder='Select Contact Method...'
                                    value={managementCompanyData?.contactMethod || null}
                                    options={createOptionListForSelectTag(enums?.["ContactMethod"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "contactMethod", value: selectedOption })
                                    }}

                                    label={"Contact Method"}
                                />

                                <TextAreaField
                                    label="Note"
                                    name="note"
                                    value={managementCompanyData?.note || ""}
                                    className="mb-2 text-surface-2"
                                    onChange={handleOnChange}
                                />


                            </div>

                            <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                </div>
                                <div className="relative col-span-9">
                                    <div className={"mt-4 flex"}>
                                        <ButtonTray
                                            buttons={[
                                                {
                                                    buttonProps: {
                                                        btnText: "Cancel",
                                                        onClick: () => navigate(AppRoutes.ManagementCompany.path),
                                                        type: "cancel",
                                                    },
                                                    buttonType: "button",
                                                },
                                                {
                                                    buttonProps: {
                                                        btnText: "Submit",
                                                        type: "submit",
                                                        isLoading: createApiResponse.isLoading,
                                                    },
                                                    buttonType: "button",
                                                },
                                            ]}
                                            align="end"
                                            gap={2}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>} />
                    </div>
                </div>
                {showProviderModal &&
                    <CommonSelectorModal
                        title="Third Party Selector"
                        columns={columns}
                        data={useSearchThirdPartyProviderApiQuery}
                        show={showProviderModal}
                        onSubmit={handleOnClick}
                        setShow={setShowProviderModal}
                        setSelectedRow={setSelectedRow}
                    />
                }
            </div>
        </>
    );
};

export default CreateManagementCompany;
